/**
 * LiveTrackingHeader — status bar for a live-tracking snapshot: runner name,
 * status, delta vs. plan, revised finish ETA, last-updated + refresh.
 * The checkpoint-by-checkpoint detail lives in RunningCheckpointsGrid instead.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { tokens } from '../../../theme';
import { formatDuration, formatArrivalTime } from '../../../utils/units';

function secondsAgo(isoString) {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  return Math.max(0, Math.round(diffMs / 1000));
}

// Formats a moment in a given IANA timezone (e.g. "Europe/Rome"). Falls back to the
// browser's own timezone if `timeZone` is missing or invalid (e.g. before the first poll).
function formatTimeInZone(date, timeZone, { showSeconds = false } = {}) {
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
    ...(timeZone ? { timeZone } : {}),
  };
  try {
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  } catch {
    delete options.timeZone;
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }
}

// Format an "elapsed since start" estimate (ours) as a race-local clock time (+ "Day N"),
// anchored on the ACTUAL start (UTC) and rendered in the race's IANA timezone — the exact
// same basis UTMB uses for its own ETAs, so the two line up. Returns null if we lack the
// real start / timezone (caller then falls back to the scheduled-start formatter).
function formatEtaInRaceTz(startUtcIso, elapsedSeconds, timeZone, { omitDayOne = false } = {}) {
  if (elapsedSeconds == null || !startUtcIso || !timeZone) return null;
  const start = new Date(startUtcIso);
  if (Number.isNaN(start.getTime())) return null;
  const arrival = new Date(start.getTime() + elapsedSeconds * 1000);
  const dayKey = (d) => {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    } catch {
      return null;
    }
  };
  const sk = dayKey(start);
  const ak = dayKey(arrival);
  const dayNum = (sk && ak) ? Math.round((Date.parse(ak) - Date.parse(sk)) / 86400000) + 1 : 1;
  const prefix = (omitDayOne && dayNum === 1) ? '' : `Day ${dayNum} `;
  return `${prefix}${formatTimeInZone(arrival, timeZone)}`;
}

export default function LiveTrackingView({ snapshot, predictionSummary, onRefresh, refreshing, workoutStartTime = null, elevation = null }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));  // match SharedLiveTracking
  // On mobile, let the stat groups wrap (instead of overflowing / scrolling
  // horizontally) and tighten the gaps so more numbers fit without panning.
  const groupWrap = isMobile ? 'wrap' : 'nowrap';
  const groupGap = isMobile ? 1.25 : 3;

  // Consistent colour convention: our own estimates (blue) vs UTMB's estimates (purple).
  // Green/red stay reserved for the "Vs. plan" delta (green = ahead, red = behind);
  // purple is a calm, neutral colour that avoids any "warning" connotation.
  const OURS_COLOR = colors.blueAccent?.[300] || '#64b5f6';
  const UTMB_COLOR = '#B690C6';

  // Ticking wall clock for the Race/Client Local Time display — independent of the data poll.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!snapshot) return null;

  const {
    link,
    runner,
    checkpoints = [],
    utmb_prediction: utmbPrediction,
    revised_finish_eta_s: revisedFinishEtaS,
    race_timezone: raceTimezone,
    ranking,
  } = snapshot;

  const lastCheckpoint = [...checkpoints]
    .reverse()
    .find((cp) => cp.live_elapsed_s !== null && cp.live_elapsed_s !== undefined) || null;

  // First checkpoint after the last one reached — carries our own updated (blended) estimate.
  const lastCheckpointIndex = lastCheckpoint ? checkpoints.indexOf(lastCheckpoint) : -1;
  const nextCheckpoint = checkpoints[lastCheckpointIndex + 1] || null;
  // The finish is the last checkpoint in the ordered list — used as the "Finish" identity label.
  const finishPoint = checkpoints.length ? checkpoints[checkpoints.length - 1] : null;

  // Checkpoint name prefixed with its distance from the start (rounded km).
  const cpWithKm = (cp) =>
    (cp?.distance_km != null ? `${Math.round(cp.distance_km)} km · ` : '') + (cp?.name || '');

  // Our ETAs: anchor on the ACTUAL start (UTC) and render in the race timezone so they
  // match UTMB's own ETAs. Fall back to the scheduled-start clock if we lack live start/tz.
  const raceStartUtc = runner?.start_time_utc || null;
  const oursEta = (elapsedSeconds, opts = {}) =>
    formatEtaInRaceTz(raceStartUtc, elapsedSeconds, raceTimezone, opts)
    ?? formatArrivalTime(elapsedSeconds, workoutStartTime, opts);

  // UTMB's own predicted finish DURATION = their finish ETA minus the actual start.
  const _utmbFinS = (utmbPrediction?.finish_eta && raceStartUtc)
    ? (Date.parse(utmbPrediction.finish_eta) - Date.parse(raceStartUtc)) / 1000 : null;
  const utmbFinishDurationS = Number.isFinite(_utmbFinS) && _utmbFinS > 0 ? _utmbFinS : null;

  const status = runner?.status || link?.status;

  const statusColor = status === 'finished' ? 'success' : status === 'error' ? 'error' : status === 'live' ? 'warning' : 'default';
  const statusLabel = {
    live: 'Racing',
    finished: 'Finished',
    dnf: 'DNF',
    error: 'Error',
    not_started: 'Not started',
  }[status] || status;

  const agoS = secondsAgo(link?.last_polled_at);

  return (
    <Paper sx={{
      p: 2, mb: 2, backgroundColor: colors.primary[800],
      // On mobile, break out to the full viewport width and blend into the page
      // background (transparent + no shadow) instead of a gray full-width band.
      ...(isMobile && {
        borderRadius: 0, width: '100vw', ml: 'calc(50% - 50vw)',
        backgroundColor: 'transparent', boxShadow: 'none',
      }),
    }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          {runner?.fullname || link?.runner_display_name || 'Live tracking'}
        </Typography>
        <Chip size="small" label={statusLabel} color={statusColor} />
        {predictionSummary?.race_name && (
          <Typography variant="body2" color={colors.grey[300]}>
            {predictionSummary.race_name}
          </Typography>
        )}
        <Box flexGrow={1} />
        {agoS !== null && (
          <Typography variant="caption" color={colors.grey[400]}>
            Updated {agoS < 60 ? `${agoS}s` : `${Math.round(agoS / 60)}m`} ago
          </Typography>
        )}
        {onRefresh && (
          <Tooltip title="Refresh now">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={refreshing}>
                <RefreshIcon fontSize="small" sx={{ color: colors.grey[300] }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {link?.status === 'error' && (
        <Typography variant="body2" color="error" mb={1}>
          {link.last_error || 'Could not reach the live tracking source.'}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
        <Typography variant="caption" color={colors.grey[500]} fontStyle="italic">
          All timings below are shown in race-local time (Viewer Local Time excepted).
        </Typography>
      </Box>

      <Box display="flex" gap={groupGap} flexWrap="wrap">
        {lastCheckpoint && (
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Last checkpoint</Typography>
            <Typography variant="body1">{cpWithKm(lastCheckpoint)}</Typography>
          </Box>
        )}
        {lastCheckpoint?.in_time && (
          <Box display="flex" gap={groupGap} flexWrap={groupWrap}>
            <Box>
              <Typography variant="caption" color={colors.grey[400]}>CP In</Typography>
              <Typography variant="body1">{formatTimeInZone(new Date(lastCheckpoint.in_time), raceTimezone)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color={colors.grey[400]}>CP Out</Typography>
              <Typography variant="body1">
                {lastCheckpoint.out_time
                  ? formatTimeInZone(new Date(lastCheckpoint.out_time), raceTimezone)
                  : <Typography component="span" variant="body2" color={colors.grey[500]}>Pending</Typography>}
              </Typography>
            </Box>
          </Box>
        )}
        {lastCheckpoint?.delta_s !== null && lastCheckpoint?.delta_s !== undefined && (
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Vs. plan</Typography>
            {lastCheckpoint.delta_s === 0 ? (
              <Typography variant="body1" fontWeight="bold" color={colors.grey[300]}>
                On plan
              </Typography>
            ) : (
              <Typography
                variant="body1"
                fontWeight="bold"
                color={lastCheckpoint.delta_s > 0 ? colors.redAccent[400] : colors.greenAccent[400]}
              >
                {formatDuration(Math.abs(lastCheckpoint.delta_s))} {lastCheckpoint.delta_s > 0 ? 'behind' : 'ahead'}
              </Typography>
            )}
          </Box>
        )}
        {lastCheckpoint?.live_elapsed_s != null && (
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Race Time</Typography>
            <Typography variant="body1" fontWeight="bold">{formatDuration(lastCheckpoint.live_elapsed_s)}</Typography>
          </Box>
        )}
        {nextCheckpoint && (
          <Box display="flex" gap={groupGap} flexWrap={groupWrap} sx={{ flexBasis: '100%' }}>
            <Box sx={{ flexBasis: isMobile ? '100%' : 'auto' }}>
              <Typography variant="caption" color={colors.grey[400]}>Next checkpoint</Typography>
              <Typography variant="body1">{cpWithKm(nextCheckpoint)}</Typography>
            </Box>
            {nextCheckpoint.estimated_elapsed_s != null && workoutStartTime && (
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>Est. arrival (ours)</Typography>
                <Typography variant="body1" fontWeight="bold" color={OURS_COLOR}>
                  {oursEta(nextCheckpoint.estimated_elapsed_s, { omitDayOne: true })}
                </Typography>
              </Box>
            )}
            {utmbPrediction?.next_point_eta && (
              <Box>
                <Typography variant="caption" color={colors.grey[400]}>UTMB's arrival ETA</Typography>
                <Typography variant="body1" fontWeight="bold" color={UTMB_COLOR}>
                  {formatTimeInZone(new Date(utmbPrediction.next_point_eta), raceTimezone)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {((revisedFinishEtaS !== null && revisedFinishEtaS !== undefined) || utmbPrediction?.finish_eta) && (
          <>
            {/* "Finish" identity label + DURATIONS (ours + UTMB's) — mirrors the Next
                checkpoint row; the label breaks to its own line on mobile. */}
            <Box display="flex" gap={groupGap} flexWrap={groupWrap} sx={{ flexBasis: '100%' }}>
              <Box sx={{ flexBasis: isMobile ? '100%' : 'auto' }}>
                <Typography variant="caption" color={colors.grey[400]}>Finish</Typography>
                <Typography variant="body1">{finishPoint ? cpWithKm(finishPoint) : 'Finish'}</Typography>
              </Box>
              {revisedFinishEtaS !== null && revisedFinishEtaS !== undefined && (
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>Revised finish (duration)</Typography>
                  <Typography variant="body1" fontWeight="bold" color={OURS_COLOR}>{formatDuration(revisedFinishEtaS)}</Typography>
                </Box>
              )}
              {utmbFinishDurationS != null && (
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>UTMB's finish (duration)</Typography>
                  <Typography variant="body1" fontWeight="bold" color={UTMB_COLOR}>{formatDuration(utmbFinishDurationS)}</Typography>
                </Box>
              )}
            </Box>
            {/* Finish TIMINGS: ours + UTMB's on the next row. */}
            <Box display="flex" gap={groupGap} flexWrap={groupWrap} sx={{ flexBasis: '100%' }}>
              {revisedFinishEtaS !== null && revisedFinishEtaS !== undefined && workoutStartTime && (
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>Revised finish (timing)</Typography>
                  <Typography variant="body1" fontWeight="bold" color={OURS_COLOR}>{oursEta(revisedFinishEtaS)}</Typography>
                </Box>
              )}
              {utmbPrediction?.finish_eta && (
                <Box>
                  <Typography variant="caption" color={colors.grey[400]}>UTMB's finish ETA</Typography>
                  <Typography variant="body1" fontWeight="bold" color={UTMB_COLOR}>
                    {formatTimeInZone(new Date(utmbPrediction.finish_eta), raceTimezone)}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
        <Box display="flex" gap={groupGap} flexWrap={groupWrap} sx={{ flexBasis: '100%' }}>
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Race Local Time</Typography>
            <Typography variant="body1">{formatTimeInZone(now, raceTimezone, { showSeconds: true })}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Viewer Local Time</Typography>
            <Typography variant="body1">{formatTimeInZone(now, null, { showSeconds: true })}</Typography>
          </Box>
        </Box>
      </Box>

      {elevation && (
        <Box display="flex" gap={groupGap} flexWrap="wrap" mt={2}>
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Distance (done / left)</Typography>
            <Typography variant="body1">
              {elevation.distDoneKm.toFixed(1)} / {elevation.distLeftKm.toFixed(1)} km
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Climb ↑ (done / left)</Typography>
            <Typography variant="body1">
              {elevation.gainPassed.toLocaleString()} / {elevation.gainRemaining.toLocaleString()} m
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color={colors.grey[400]}>Descent ↓ (done / left)</Typography>
            <Typography variant="body1">
              {elevation.lossPassed.toLocaleString()} / {elevation.lossRemaining.toLocaleString()} m
            </Typography>
          </Box>
        </Box>
      )}

      {ranking && (
        <Box display="flex" gap={groupGap} flexWrap="wrap" mt={2}>
          {['global', 'category', 'division'].map((key) => {
            const r = ranking[key];
            if (!r || r.value == null) return null;
            const arrowColor = r.trend === 'up' ? colors.greenAccent[400] : r.trend === 'down' ? colors.redAccent[400] : undefined;
            return (
              <Box key={key}>
                <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: 'uppercase' }}>
                  {r.label} Rank
                </Typography>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="body1" fontWeight="bold">#{r.value}</Typography>
                  {r.trend === 'up' && <ArrowUpwardIcon sx={{ fontSize: 16, color: arrowColor }} />}
                  {r.trend === 'down' && <ArrowDownwardIcon sx={{ fontSize: 16, color: arrowColor }} />}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
